import { UTCDate } from '@date-fns/utc';
import { addDays, addHours, differenceInDays, eachDayOfInterval, startOfHour } from 'date-fns';

export const eachUTCDayOfInterval = (start: Date, end: Date) =>
  eachDayOfInterval({ start: new UTCDate(start), end: new UTCDate(end) }).filter(
    (date) => date >= start,
  );

export const getDateAxisTicks = <T>(dates: T[], numberOfTicks = 15) => {
  const datesPerTick = Math.ceil(dates.length / numberOfTicks);
  const datesOutsideOfTicks = (dates.length - 1) % datesPerTick;

  const selectedIndices = [...Array(numberOfTicks).keys()] // [0,1,2,...,10]
    .map((i) => i * datesPerTick) // [0,2,4,...,20]
    .map((i) => i + Math.ceil(datesOutsideOfTicks / 2)) // [1,3,5,...,21]
    .filter((i) => i < dates.length);

  return selectedIndices.map((tickIndex) => dates[tickIndex]);
};

export const getEmptyTickMap = <T>(
  chartStartDate: Date,
  chartEndDate: Date,
  setEmptyTick: (date: Date) => T,
) => {
  const map = new Map<number, T>();
  const daysBack = differenceInDays(chartEndDate, chartStartDate);

  const startTimestamp =
    daysBack === 1
      ? // for 24 hours, we truncate timestamps to the hour
        startOfHour(chartEndDate).valueOf()
      : // otherwise the server truncates to the UTC day
        Date.UTC(
          chartEndDate.getFullYear(),
          chartEndDate.getMonth(),
          chartEndDate.getDate(),
        ).valueOf();
  const start = startTimestamp - daysBack * 3600 * 24 * 1000;

  const stepper = daysBack === 1 ? addHours : addDays;

  const stop = chartEndDate.getTime();

  // we rely on the order of the map to ensure the data is sorted correctly
  for (let current = start; current <= stop; current = stepper(current, 1).valueOf()) {
    const currentDate = new Date(current);

    map.set(
      daysBack === 1
        ? current.valueOf()
        : Date.UTC(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate(),
          ).valueOf(),
      setEmptyTick(new Date(current)),
    );
  }

  return map;
};

export const getNFoldTickValues = (maxValue: number, step = 2) => {
  if (maxValue <= 0 || step <= 0) return [0];
  const upperBound = Math.ceil(maxValue / step) * step;

  const ticks = [];
  for (let i = 0; i <= upperBound; i += step) {
    ticks.push(i);
  }
  return ticks;
};

export const colors = ['#43E1E1', '#FFD2A8', '#57FFAE', '#634DBA', '#FF6157', '#595959'];
export const INDEX_SCALING = { round: false, type: 'band' } as const;

const othersColor = colors[colors.length - 1];
// TODO: placeholder colours so per-day top 5 charts (which can show more than 5 series) don't run
// out of colours, replace with a proper palette from design
const seriesColors = [
  ...colors.slice(0, -1),
  '#4C8CFF',
  '#FF9F43',
  '#E85CCB',
  '#C6E85C',
  '#A08BFF',
  '#2FB3A0',
  '#FFE066',
  '#B07A4F',
  '#8FD3FF',
];

export const getSeriesColors = (keys: string[]) =>
  keys.map((key, index) =>
    key === 'Others' ? othersColor : seriesColors[index % seriesColors.length],
  );

export const getTopKeysPerBucket = <T, K extends string>(
  items: T[],
  {
    getBucket,
    getKey,
    getValue,
    count = 5,
  }: {
    getBucket: (item: T) => string;
    getKey: (item: T) => K | null | undefined;
    getValue: (item: T) => number;
    count?: number;
  },
) => {
  const valueByKeyByBucket = new Map<string, Map<K, number>>();
  for (const item of items) {
    const key = getKey(item);
    if (key != null) {
      const bucket = getBucket(item);
      const valueByKey = valueByKeyByBucket.get(bucket) ?? new Map<K, number>();
      valueByKey.set(key, (valueByKey.get(key) ?? 0) + getValue(item));
      valueByKeyByBucket.set(bucket, valueByKey);
    }
  }

  const topKeysByBucket = new Map<string, Set<K>>();
  const totalByKey = new Map<K, number>();
  for (const [bucket, valueByKey] of valueByKeyByBucket) {
    const topKeys = [...valueByKey]
      .filter(([, value]) => value > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, count)
      .map(([key]) => key);
    topKeysByBucket.set(bucket, new Set(topKeys));
    for (const [key, value] of valueByKey) {
      totalByKey.set(key, (totalByKey.get(key) ?? 0) + value);
    }
  }

  const keys = [...new Set([...topKeysByBucket.values()].flatMap((topKeys) => [...topKeys]))].sort(
    (a, b) => (totalByKey.get(b) ?? 0) - (totalByKey.get(a) ?? 0),
  );

  return { topKeysByBucket, keys };
};
